/**
 * schema.org Product JSON-LD builder.
 *
 * Goal: drive the Google product rich snippet seen on competitor sites —
 *   "$14.00 · In stock · Free delivery over $100"
 *
 * Google's rules (see https://developers.google.com/search/docs/appearance/structured-data/product):
 *   - `offers.price` + `priceCurrency` + `availability` are required.
 *   - `priceValidUntil` boosts the chance of the price showing.
 *   - `shippingDetails` with a free-shipping threshold drives the "Free
 *     delivery over $X" tag in the SERP. We model that via
 *     `OfferShippingDetails` + an `eligibleTransactionVolume` that gates
 *     the $0 shipping rate.
 *   - `hasMerchantReturnPolicy` removes the most common rich-result warning
 *     in Search Console.
 *
 * The site's offer is: free delivery on any order ≥ $100, served only to
 * NY ZIP codes (centered on the two retail stores). We hard-code that
 * threshold here — change `FREE_SHIPPING_OVER` to update everywhere.
 */
const FREE_SHIPPING_OVER = 100; // USD; ties to "$100 min" stated on landing page

// One year out — Google's "priceValidUntil" wants a future date; we don't
// guarantee prices to a specific day, so rolling 12 months is safe.
function priceValidUntil() {
    const d = new Date();
    d.setUTCFullYear(d.getUTCFullYear() + 1);
    return d.toISOString().slice(0, 10);
}

export function productSchema(product, store, checkoutUrl) {
    const v = product.variants?.[0];
    const price = v ? Number(v.specialPriceRec || v.priceRec) : null;
    const inStock = !!(v && v.quantity > 0);

    const shippingDetails = {
        "@type": "OfferShippingDetails",
        shippingDestination: {
            "@type": "DefinedRegion",
            addressCountry: "US",
            addressRegion: "NY",
        },
        shippingRate: {
            "@type": "MonetaryAmount",
            value: "0",
            currency: "USD",
        },
        // Drives the "Free delivery over $100" badge: the $0 shipping rate
        // only applies above this transaction volume.
        eligibleTransactionVolume: {
            "@type": "PriceSpecification",
            minPrice: FREE_SHIPPING_OVER,
            priceCurrency: "USD",
        },
        deliveryTime: {
            "@type": "ShippingDeliveryTime",
            handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 1,
                unitCode: "DAY",
            },
            transitTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 1,
                unitCode: "DAY",
            },
        },
    };

    const returnPolicy = {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        // NY cannabis: all sales final per state regulation — we still
        // declare the policy so Google's structured data validator stops
        // warning about a missing policy.
        returnPolicyCategory:
            "https://schema.org/MerchantReturnNotPermitted",
    };

    const offers = v
        ? {
              "@type": "Offer",
              price: price?.toFixed ? price.toFixed(2) : price,
              priceCurrency: "USD",
              priceValidUntil: priceValidUntil(),
              availability: inStock
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
              itemCondition: "https://schema.org/NewCondition",
              url: checkoutUrl,
              seller: {
                  "@type": "Organization",
                  name: store?.name || "ZenZest",
              },
              shippingDetails,
              hasMerchantReturnPolicy: returnPolicy,
          }
        : undefined;

    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product.description,
        image: (product.images || []).map((i) => i.url),
        brand: product.brand?.name && {
            "@type": "Brand",
            name: product.brand.name,
        },
        category: product.category,
        sku: product.id,
        mpn: product.id,
        offers,
    };
}
